import React, { useState } from "react";
import { Script, graphql } from "gatsby";
import getContentPageComponents from "../getContentPageComponents";
import Layout from "../components/Layout";
import { MediaContextProvider } from "../components/Media";
import Seo from "../components/Seo";

function ContentPage({ data, location }) {
  const page = data.contentfulContentPage;

  const [inViewItem, setInViewItem] = useState(null);

  const { components } = page;
  const onChangeInView = (inView, anchorId) => {
    if (inView) setInViewItem(anchorId);
  };

  const componentsToRender = getContentPageComponents(
    components,
    true,
    location,
    onChangeInView,
    inViewItem
  );
  return (
    <>
      <Layout page={page} location={location}>
        <MediaContextProvider>{componentsToRender}</MediaContextProvider>
      </Layout>
      <Script
        id="google-recaptcha-script"
        src="https://www.google.com/recaptcha/api.js?render=6Le4dpgaAAAAAOfaTgv75r2SYLkYC0CVVY3Lvcrv"
      />
    </>
  );
}

export const Head = (props) => (
  <>
    <Seo {...props} />
  </>
);

export default ContentPage;

export const query = graphql`
  query ContentPageBySlug($slug: String!) {
    contentfulContentPage(slug: { eq: $slug }) {
      components {
        ... on Node {
          ... on ContentfulJumbotron {
            __typename
            id
            backgroundColour
            hasBottomMargin
            backgroundImage {
              gatsbyImageData(layout: FULL_WIDTH)
            }
            secondaryImage {
              gatsbyImageData(layout: FULL_WIDTH)
            }
            title
            description {
              childMarkdownRemark {
                html
              }
            }
            ctaLabel
            ctaUrl
            ctaChatbotIdentifier
          }
          ... on ContentfulHero {
            __typename
            id
            backgroundColour
            eyebrow
            hasBottomMargin
            title
            description {
              childMarkdownRemark {
                html
              }
            }
            ctaLabel
            ctaUrl
          }
          ... on ContentfulSubnavigation {
            __typename
            id
            subnavItems {
              id
              colour
              title
              component {
                ... on ContentfulSolutionArea {
                  __typename
                  id
                  anchorId
                }
              }
            }
          }
          ... on ContentfulClientLogos {
            __typename
            id
            title
            clientLogo {
              id
              companyName
              subTitle
              logo {
                gatsbyImageData
                id
                description
                file {
                  url
                }
              }
            }
            hasBottomMargin
            showTitle
            backgroundColour
            numberOfColumns
          }
          ... on ContentfulContentArea {
            __typename
            id
            ctaUrl
            ctaLabel
            ctaChatbotIdentifier
            eyebrow
            title
            hideTitle
            hideIndexNumberDisplay
            backgroundColour
            hasBottomMargin
            hasTopMargin
            description {
              childMarkdownRemark {
                html
              }
            }
            subDescription {
              childMarkdownRemark {
                html
              }
            }
            image {
              id
              description
              gatsbyImageData
            }
            video {
              file {
                contentType
                url
              }
            }
            alignment
            layout
            anchorId
          }
          ... on ContentfulStatistics {
            __typename
            id
            hasBottomMargin
            hasTopMargin
            statistics {
              number
              title
              secondSource {
                childMarkdownRemark {
                  html
                }
                id
              }
              id
              firstSource {
                id
                childMarkdownRemark {
                  html
                }
              }
            }
          }
          ... on ContentfulQuote {
            __typename
            id
            name
            referenceDate(formatString: "MMMM DD, YYYY")
            dateLabel
            referenceTitle
            referenceTitleLabel
            referenceUrl
            title
            position
            layout            
            hasBottomMargin
            hasTopMargin
            imagePosition
            company
            image {
              id
              gatsbyImageData
            }
            logo {
              description
              gatsbyImageData
            }
            eyebrow
            quote {
              childMarkdownRemark {
                html
              }
            }
          }
          ... on ContentfulSolutionArea {
            __typename
            id
            eyebrow
            title
            description {
              childMarkdownRemark {
                html
              }
            }
            capabilities {
              raw
            }
            image {
              id
              gatsbyImageData
              description
            }
            anchorId
          }
          ... on ContentfulCards {
            __typename
            id
            backgroundColour
            title
            titleColour
            eyebrow
            hideTitle
            hideIndexNumberDisplay
            descriptionColour
            description {
              childMarkdownRemark {
                html
              }
            }
            cards {
              ... on ContentfulCard {
                __typename
                backgroundColour
                title
                titleColour
                eyebrow
                id
                descriptionColour
                description {
                  childMarkdownRemark {
                    html
                  }
                }
                bulletedList
                cta {
                  ctaUrl
                  ctaLabel
                }
                ctaLabel
                ctaUrl
                image {
                  id
                  description
                  gatsbyImageData
                }
              }
              ... on ContentfulStatistic {
                __typename
                number
                title
                secondSource {
                  childMarkdownRemark {
                    html
                  }
                  id
                }
                id
                firstSource {
                  id
                  childMarkdownRemark {
                    html
                  }
                }
              }
            }
            layout
            backgroundColour
            hasBottomMargin
            hasTopMargin
            numberOfColumns
            image {
              id
              description
              gatsbyImageData
            }
            mobileImage {
              id
              description
              gatsbyImageData
            }          
            cta {
              ctaUrl
              ctaLabel
              backgroundColour
            }
          }
          ... on ContentfulFaQs {
            __typename
            id
            title
            hasBottomMargin
            hasTopMargin
            questions {
              question
              id
              answer {
                childMarkdownRemark {
                  html
                }
              }
            }
          }
          ... on ContentfulNextBestAction {
            __typename
            id
            ctaUrl
            ctaLabel
          }
          ... on ContentfulBigImage {
            __typename
            id
            title
            imageMask
            image {
              gatsbyImageData
              description
            }
            contentAlignment
            ctaUrl
            ctaLabel
            hasBottomMargin
            imageOverlay {
              gatsbyImageData
            }
            layout
            components {
              ... on ContentfulContentArea {
                __typename
                id
                eyebrow
                title
                description {
                  childMarkdownRemark {
                    html
                  }
                }
              }
              ... on ContentfulQuote {
                __typename
                id
                name
                company
                image {
                  id
                  description
                  gatsbyImageData
                }
                quote {
                  childMarkdownRemark {
                    html
                  }
                }
              }
            }
          }
          ... on ContentfulPartnership {
            __typename
            id
            quote {
              ... on ContentfulQuote {
                __typename
                company
                name
                eyebrow
                quote {
                  childMarkdownRemark {
                    html
                  }
                }
                position
              }
            }
            logo {
              id
              description
              gatsbyImageData
            }
            description {
              childMarkdownRemark {
                html
              }
            }
            title
            eyebrow
            hasBottomMargin
            hasTopMargin
            richText {
              raw
            }
          }
          ... on ContentfulMarqueeTitleSection {
            __typename
            title
            id
            hasBottomMargin
            hasTopBorder
            backgroundColour
            anchorId
            content {
              ... on ContentfulBigImage {
                __typename
                id
                ctaLabel
                ctaUrl
                title
                imageMask
                image {
                  gatsbyImageData
                }
                layout
              }
              ... on ContentfulCareersList {
                __typename
                id
              }
              ... on ContentfulContentArea {
                __typename
                id
                ctaUrl
                ctaLabel
                ctaChatbotIdentifier
                eyebrow
                title
                hideTitle
                backgroundColour
                description {
                  childMarkdownRemark {
                    html
                  }
                }
                subDescription {
                  childMarkdownRemark {
                    html
                  }
                }
                image {
                  id
                  description
                  gatsbyImageData
                }
                video {
                  file {
                    contentType
                    url
                  }
                }
                alignment
                layout
                anchorId
              }
              ... on ContentfulCards {
                __typename
                id
                backgroundColour
                title
                titleColour
                eyebrow
                hideTitle
                hideIndexNumberDisplay
                description {
                  childMarkdownRemark {
                    html
                  }
                }
                descriptionColour
                layout                
                cta {
                  ctaUrl
                  ctaLabel
                  backgroundColour
                } 
                cards {
                  ... on ContentfulCard {
                    __typename
                    backgroundColour
                    title
                    titleColour
                    eyebrow
                    id
                    description {
                      childMarkdownRemark {
                        html
                      }
                    }
                    descriptionColour
                    bulletedList
                    cta {
                      ctaUrl
                      ctaLabel
                    }
                    ctaLabel
                    ctaUrl
                    image {
                      id
                      description
                      gatsbyImageData
                    }
                  }
                }
              }
            }
            description {
              childMarkdownRemark {
                html
              }
            }
            subdescription {
              childMarkdownRemark {
                html
              }
            }
          }
          ... on ContentfulLeadership {
            __typename
            id
            title
            people {
              id
              position
              name
              url
              image {
                gatsbyImageData
              }
            }
          }
          ... on ContentfulScrollableContent {
            __typename
            id
            backgroundColour
            image {
              gatsbyImageData
              title
            }
            title
            hideTitle
            contentAreas {
              id
              eyebrow
              title
              description {
                childMarkdownRemark {
                  html
                }
              }
            }
            ctaUrl
            ctaLabel
            layout
            hasBottomMargin
          }
          ... on ContentfulContactUsForm {
            __typename
            id
            title
            eyebrow
            hasBottomMargin
            hasTopMargin
            successMessage
            showSocialLinks
            contentArea {
              ... on ContentfulContentArea {
                __typename
                id
                ctaUrl
                ctaLabel
                ctaChatbotIdentifier
                eyebrow
                title
                hideTitle
                hideIndexNumberDisplay
                backgroundColour
                hasBottomMargin
                hasTopMargin
                description {
                  childMarkdownRemark {
                    html
                  }
                }
                subDescription {
                  childMarkdownRemark {
                    html
                  }
                }
                image {
                  id
                  description
                  gatsbyImageData
                }
                video {
                  file {
                    contentType
                    url
                  }
                }
                alignment
                layout
                anchorId
              }
            }
          }
          ... on ContentfulImpactStorySummary {
            __typename
            id
            backgroundColour
            title
            ctaUrl
            ctaLabel
            ctaOpensChatWindow
            hasBottomMargin
            hasTopMargin
            description {
              childMarkdownRemark {
                html
              }
            }
            imagePosition
            image {
              id
              description
              gatsbyImageData
            }
            logos {
              id
              description
              gatsbyImageData
            }
          }
          ... on ContentfulImpactStoryJumbotron {
            __typename
            id
            title
            textColor 
            titleElement
            eyebrow
            hasBottomMargin
            imageMask
            backgroundImage {
              id
              description
              gatsbyImageData(layout: FULL_WIDTH)
            }
            cta {
              ctaUrl
              ctaLabel
              backgroundColour
            }
            description {
              childMarkdownRemark {
                html
              }
            }
          }
          ... on ContentfulComponentGroup {
            __typename
            id
            groupedComponents: content {
              ... on ContentfulComponentGroupHeading {
                __typename
                id
                title
                eyebrow
                bottomSpacing
              }
              ... on ContentfulParagraph {
                __typename
                id
                bottomSpacing
                paragraph {
                  childMarkdownRemark {
                    html
                  }
                }
              }
              ... on ContentfulBigImage {
                __typename
                id
                title
                imageMask
                image {
                  gatsbyImageData
                  title
                }
                contentAlignment
                ctaUrl
                ctaLabel
                hasBottomMargin
                imageOverlay {
                  gatsbyImageData
                }
                layout
                components {
                  ... on ContentfulContentArea {
                    __typename
                    id
                    eyebrow
                    title
                    description {
                      childMarkdownRemark {
                        html
                      }
                    }
                  }
                  ... on ContentfulQuote {
                    __typename
                    id
                    name
                    company
                    quote {
                      childMarkdownRemark {
                        html
                      }
                    }
                  }
                }
              }
              ... on ContentfulScrollableContent {
                __typename
                id
                backgroundColour
                image {
                  gatsbyImageData
                  title
                }
                title
                hideTitle
                contentAreas {
                  id
                  eyebrow
                  title
                  description {
                    childMarkdownRemark {
                      html
                    }
                  }
                }
                ctaUrl
                ctaLabel
                layout
                hasBottomMargin
              }
            }
          }
          ... on ContentfulCta {
            __typename
            id
            backgroundColour
            ctaLabel
            ctaUrl
          }
          ... on ContentfulPartnershipLogos {
            __typename
            id
            title
            ctaUrl
            ctaLabel
            logos {
              id
              description
              gatsbyImageData
            }
          }
          ... on ContentfulRichTextArea {
            __typename
            id
            richText {
              raw
              references {
                ... on ContentfulAsset {
                  contentful_id
                  title
                  description
                  gatsbyImageData
                  __typename
                }
                ... on ContentfulPullQuote {
                  contentful_id
                  __typename
                  position
                  quote {
                    id
                    quote
                    childMarkdownRemark {
                      html
                    }
                  }
                }
              }
            }
          }
        }
      }
      name
      slug
      ogtitle
      ogurl
      ogdescription
      ogtype
      ogimage {
        gatsbyImageData
      }
      pageTitle
      metaDescription
      metaKeywords
      transparentNavigation
      chatbotIdentifier
    }
    site {
      siteMetadata {
        title
        siteUrl
      }
    }
  }
`;
